import { useCallback, useEffect, useId, useState } from 'react';
import type { KeyboardEvent } from 'react';

// Keyboard + ARIA wiring for our custom dropdowns (WAI-ARIA APG "Combobox" pattern).
// Focus stays on the combobox element (a search input, or the trigger button for a
// select-only dropdown); the highlighted option is exposed via `aria-activedescendant`.
// - ArrowDown/ArrowUp move the highlight (skipping disabled items); ArrowDown opens when closed.
// - Enter selects the highlighted item (the first match by default, as before).
// - Escape closes and is marked handled so an enclosing dialog doesn't also close.
// - selectOnly (no text input): Enter/Space also open, and Space selects.
// Components keep their own rendering; they only spread the returned prop getters.

interface UseListboxNavigationOptions<T> {
  items: T[];
  isOpen: boolean;
  onSelect: (item: T) => void;
  onOpen?: () => void;
  onClose: () => void;
  isItemDisabled?: (item: T) => boolean;
  // Item highlighted when the list opens (e.g. the current value); defaults to the first enabled.
  selectedIndex?: number;
  selectOnly?: boolean;
}

export function useListboxNavigation<T>({
  items,
  isOpen,
  onSelect,
  onOpen,
  onClose,
  isItemDisabled,
  selectedIndex,
  selectOnly = false,
}: UseListboxNavigationOptions<T>) {
  const listboxId = useId();
  const [activeIndex, setActiveIndex] = useState(-1);

  const isEnabled = useCallback(
    (index: number) => index >= 0 && index < items.length && !isItemDisabled?.(items[index]),
    [items, isItemDisabled]
  );

  // Next enabled index from `from` in `step` direction; stays put at either end.
  const findEnabled = useCallback(
    (from: number, step: 1 | -1) => {
      for (let i = from; i >= 0 && i < items.length; i += step) {
        if (isEnabled(i)) return i;
      }
      return -1;
    },
    [items.length, isEnabled]
  );

  // Until the user moves, the highlight is derived at render time (items may arrive a render
  // after opening): the current value on open, the first match after the search changes.
  const [seed, setSeed] = useState<'selected' | 'first'>('selected');

  // Components call this when the search text changes, so Enter picks the first match.
  const resetActive = useCallback(() => {
    setActiveIndex(-1);
    setSeed('first');
  }, []);

  useEffect(() => {
    setActiveIndex(-1);
    setSeed('selected');
  }, [isOpen]);

  const seedIndex =
    seed === 'selected' && selectedIndex !== undefined && isEnabled(selectedIndex)
      ? selectedIndex
      : findEnabled(0, 1);
  // A filtered list can shrink under the highlight: fall back to the seed.
  const current = isEnabled(activeIndex) ? activeIndex : seedIndex;

  const optionId = (index: number) => `${listboxId}-opt-${index}`;

  // Keep the highlighted option visible in scrollable lists (scrollIntoView is absent in jsdom).
  useEffect(() => {
    if (!isOpen || current < 0) return;
    document.getElementById(`${listboxId}-opt-${current}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [isOpen, current, listboxId]);

  const move = (step: 1 | -1) => {
    const next = findEnabled(current + step, step);
    if (next >= 0) setActiveIndex(next);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (!isOpen) {
      const opens = e.key === 'ArrowDown' || (selectOnly && (e.key === 'Enter' || e.key === ' '));
      if (opens && onOpen) {
        e.preventDefault();
        onOpen();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(-1);
        break;
      case 'Enter':
        e.preventDefault();
        if (current >= 0) onSelect(items[current]);
        break;
      case ' ':
        if (!selectOnly) break;
        e.preventDefault();
        if (current >= 0) onSelect(items[current]);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        onClose();
        break;
    }
  };

  const getComboboxProps = () => ({
    role: 'combobox' as const,
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? listboxId : undefined,
    'aria-haspopup': 'listbox' as const,
    'aria-autocomplete': selectOnly ? undefined : ('list' as const),
    'aria-activedescendant': isOpen && current >= 0 ? optionId(current) : undefined,
    onKeyDown,
  });

  const getListboxProps = (label?: string) => ({
    id: listboxId,
    role: 'listbox' as const,
    'aria-label': label,
  });

  const getOptionProps = (index: number, selected = false) => ({
    id: optionId(index),
    role: 'option' as const,
    'aria-selected': selected,
    'aria-disabled': isItemDisabled?.(items[index]) || undefined,
    onMouseMove: () => isEnabled(index) && index !== current && setActiveIndex(index),
  });

  return {
    activeIndex: current,
    isActive: (index: number) => index === current,
    resetActive,
    getComboboxProps,
    getListboxProps,
    getOptionProps,
  };
}
